import React, { useState, useEffect } from 'react';
import { Tooltip as BsTooltip } from 'bootstrap';
import {
  UserBasicInfoResp,
  UserGradeInfoResp,
  UserProjectGradeInfoResp
} from 'domain/payload/response/user/UserViewResp';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';

export interface UserDeviceInfoCardProps {
  userInfo: UserBasicInfoResp | null;
  gradeInfo?: UserGradeInfoResp | null;
  gradeProjects?: UserProjectGradeInfoResp[];
}

interface GradeTierConfig {
  badgeBg: string;
  badgeText: string;
  cardBg: string;
  borderColor: string;
  label: string;
  description: string;
}

const getGradeConfig = (gradeStr?: string): GradeTierConfig => {
  const clean = (gradeStr || '').toUpperCase().replace('GRADE', '').trim();
  if (clean.startsWith('A')) {
    return {
      badgeBg: '#10B981',
      badgeText: '#FFFFFF',
      cardBg: '#F0FDF4',
      borderColor: '#BBF7D0',
      label: 'High Performer',
      description: 'Independent and consistently delivers high quality work with minimal rejections.',
    };
  }
  if (clean.startsWith('B')) {
    return {
      badgeBg: '#EAB308',
      badgeText: '#FFFFFF',
      cardBg: '#FEFCE8',
      borderColor: '#FEF08A',
      label: 'Competent',
      description: 'Stable performance and reliable in completing tasks for regular operations.',
    };
  }
  if (clean.startsWith('C')) {
    return {
      badgeBg: '#F97316',
      badgeText: '#FFFFFF',
      cardBg: '#FFF7ED',
      borderColor: '#FED7AA',
      label: 'Needs Supervision',
      description: 'Requires supervision or improvement in work accuracy/SLA.',
    };
  }
  return {
    badgeBg: '#EF4444',
    badgeText: '#FFFFFF',
    cardBg: '#FEF2F2',
    borderColor: '#FECACA',
    label: 'Needs Evaluation',
    description: 'High rejection or drop rate. Requires evaluation or retraining.',
  };
};

const getProgressBarColor = (val: number): string => {
  if (val >= 85) return '#10B981';
  if (val >= 70) return '#EAB308';
  if (val >= 55) return '#F97316';
  return '#EF4444';
};

interface PillarGradeInfo {
  letter: string;
  label: string;
  badgeBg: string;
}

const getPillarGrade = (score: number): PillarGradeInfo => {
  if (score >= 85) return { letter: 'A', label: 'Grade A', badgeBg: '#10B981' };
  if (score >= 70) return { letter: 'B', label: 'Grade B', badgeBg: '#EAB308' };
  if (score >= 55) return { letter: 'C', label: 'Grade C', badgeBg: '#F97316' };
  return { letter: 'D', label: 'Grade D', badgeBg: '#EF4444' };
};

const UserDeviceInfoCard: React.FC<UserDeviceInfoCardProps> = ({
  userInfo,
  gradeInfo,
  gradeProjects = []
}) => {
  const [activeTab, setActiveTab] = useState<'grade' | 'device'>(() => (gradeInfo ? 'grade' : 'device'));
  const [showGuide, setShowGuide] = useState<boolean>(false);
  const { navRef, indicatorStyle } = useNavPillsIndicator(activeTab);

  const hasDeviceInfo = userInfo?.deviceInfo !== null && userInfo?.deviceInfo !== undefined;
  const hasGradeInfo = !!gradeInfo;

  useEffect(() => {
    if (gradeInfo) {
      setActiveTab('grade');
    }
  }, [gradeInfo]);

  useEffect(() => {
    const TooltipClass = (window as any).bootstrap?.Tooltip || BsTooltip;
    if (!TooltipClass) return;

    const timer = setTimeout(() => {
      const elements = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      elements.forEach((el) => {
        try {
          const old = TooltipClass.getInstance(el);
          if (old) {
            old.dispose();
          }
          new TooltipClass(el, {
            trigger: 'hover',
            html: true,
            customClass: 'tooltip-mechanism',
          });
        } catch (err) {
          // ignore
        }
      });
    }, 150);

    return () => {
      clearTimeout(timer);
      const elements = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      elements.forEach((el) => {
        try {
          const inst = TooltipClass.getInstance(el);
          if (inst) {
            inst.dispose();
          }
        } catch (err) {
          // ignore
        }
      });
    };
  }, [gradeProjects, gradeInfo, activeTab]);

  const tier = gradeInfo ? getGradeConfig(gradeInfo.overall) : null;
  const qualityTier = gradeInfo ? getPillarGrade(gradeInfo.qualityRate) : null;
  const completionTier = gradeInfo ? getPillarGrade(gradeInfo.completionRate) : null;
  const commitmentTier = gradeInfo ? getPillarGrade(gradeInfo.commitmentRate) : null;
  const experienceTier = gradeInfo ? getPillarGrade(gradeInfo.experienceRate) : null;

  const qualityMechanismTooltip = gradeInfo
    ? `<div class="text-start text-white"><div class="fw-bold mb-1 border-bottom border-secondary pb-1">Quality Calculation (35% Weight)</div><div class="mb-1"><strong>Formula:</strong> (Approved Submissions / Total Submissions) &times; 100</div><div class="mb-1 text-white-50">Measures work accuracy from QC reviews. Tasks approved by QC increase the score, while rejections reduce it. (Default score is 100 if no submissions yet).</div><div class="small pt-1 border-top border-secondary text-white">Approved: <strong>${gradeInfo.totalApproved ?? 0}</strong> | Submitted: <strong>${gradeInfo.totalSubmitted ?? 0}</strong> | Rejected: <strong>${gradeInfo.totalRejected ?? 0}</strong></div></div>`
    : '';

  const completionMechanismTooltip = gradeInfo
    ? `<div class="text-start text-white"><div class="fw-bold mb-1 border-bottom border-secondary pb-1">Completion Calculation (30% Weight)</div><div class="mb-1"><strong>Formula:</strong> (Completed Tasks / Evaluated Tasks) &times; 100</div><div class="mb-1 text-white-50">Measures delivery reliability without abandoned work. For running projects, tasks currently in progress are excluded so active work is not penalized; expired or dropped tasks reduce the score.</div><div class="small pt-1 border-top border-secondary text-white">Completed: <strong>${gradeInfo.completedTasks ?? 0}</strong> | Dropped/Expired: <strong>${gradeInfo.droppedTasks ?? 0}</strong> | Assigned: <strong>${gradeInfo.totalAssigned ?? 0}</strong></div></div>`
    : '';

  const commitmentMechanismTooltip = gradeInfo
    ? `<div class="text-start text-white"><div class="fw-bold mb-1 border-bottom border-secondary pb-1">Commitment Calculation (25% Weight)</div><div class="mb-1"><strong>Formula:</strong> Target Quota Achievement</div><div class="mb-1 text-white-50">Measures completed volume against targets. Uses configured project quotas, default 5&ndash;10 tasks, or proportional targets (20%&ndash;80%) if assigned &lt;5 tasks. Meeting target awards 100 pts, with bonus up to 115 pts for exceeding.</div><div class="small pt-1 border-top border-secondary text-white">Completed: <strong>${gradeInfo.completedTasks ?? 0} tasks</strong> (Target: 5&ndash;10 tasks)</div></div>`
    : '';

  const experienceMechanismTooltip = gradeInfo
    ? `<div class="text-start text-white"><div class="fw-bold mb-1 border-bottom border-secondary pb-1">Experience Calculation (10% Weight)</div><div class="mb-1"><strong>Formula:</strong> Cumulative Lifetime Approved Tasks</div><div class="mb-1 text-white-50">Reflects career track record across all projects:<br/>&bull; &ge;100 tasks = 100 pts<br/>&bull; 50&ndash;99 tasks = 85 pts<br/>&bull; 20&ndash;49 tasks = 70 pts<br/>&bull; 5&ndash;19 tasks = 50 pts<br/>&bull; &lt;5 tasks = 30 pts</div><div class="small pt-1 border-top border-secondary text-white">Total Lifetime Approved: <strong>${gradeInfo.totalApprovedResults ?? 0} tasks</strong></div></div>`
    : '';

  const gradeThresholdsTooltip = gradeInfo
    ? `<div class="text-start text-white"><div class="fw-bold mb-1 border-bottom border-secondary pb-1">Performance Grade Standards (0&ndash;100 Pts)</div><div class="mb-2 text-white-50" style="font-size: 11px;">Final grade determined by total weighted score:</div><div class="d-flex flex-column gap-1 mb-2 text-white" style="font-size: 11px;"><div class="d-flex justify-content-between align-items-center py-1 border-bottom border-secondary-subtle"><span>&bull; Grade A (High Performer)</span><span class="fw-semibold">&ge; 85.0 pts</span></div><div class="d-flex justify-content-between align-items-center py-1 border-bottom border-secondary-subtle"><span>&bull; Grade B (Competent)</span><span class="fw-semibold">70.0 &ndash; 84.9 pts</span></div><div class="d-flex justify-content-between align-items-center py-1 border-bottom border-secondary-subtle"><span>&bull; Grade C (Needs Supervision)</span><span class="fw-semibold">55.0 &ndash; 69.9 pts</span></div><div class="d-flex justify-content-between align-items-center py-1"><span>&bull; Grade D (Needs Evaluation)</span><span class="fw-semibold">&lt; 55.0 pts</span></div></div><div class="small pt-1 border-top border-secondary text-white d-flex justify-content-between align-items-center"><span>Current Score: <strong>${Math.min(100, gradeInfo.score).toFixed(1)} Pts</strong> &rarr; <strong>${gradeInfo.overall}</strong></span><span class="text-white-50 ms-2"><i class="fa-regular fa-clock me-1"></i>Updated daily 04:00 AM WIB</span></div></div>`
    : '';

  return (
    <div className="card h-100 border shadow-xs">
      {/* Card Header */}
      <div className="card-header bg-white border-bottom py-2">
        <div className="d-flex justify-content-between align-items-center">
          {hasGradeInfo ? (
            <div ref={navRef} className="nav-pills-animated">
              <div className="nav-pill-indicator" style={indicatorStyle} />
              <button
                type="button"
                className={`nav-link btn py-1 px-3 ${activeTab === 'grade' ? 'active' : ''}`}
                onClick={() => setActiveTab('grade')}
              >
                <i className="fa-solid fa-award me-1"></i> Grade Info
                <span
                  className="badge bg-primary text-white ms-1"
                  style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '0.5px', padding: '2px 4px', borderRadius: '3px' }}
                >
                  BETA
                </span>
              </button>
              <button
                type="button"
                className={`nav-link btn py-1 px-3 ${activeTab === 'device' ? 'active' : ''}`}
                onClick={() => setActiveTab('device')}
              >
                <i className="fa-solid fa-mobile-screen me-1"></i> Device Info
              </button>
            </div>
          ) : (
            <h6 className="mb-0 fw-bold">Device Info</h6>
          )}

          {hasGradeInfo && activeTab === 'grade' && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary py-1 px-2"
              onClick={() => setShowGuide(!showGuide)}
              title="Formula & Weight Information"
              style={{ fontSize: '11px' }}
            >
              <i className="fa-solid fa-circle-info me-1"></i>
              {showGuide ? 'Close Guide' : 'Formula Guide'}
            </button>
          )}
        </div>
      </div>

      {/* Card Body */}
      <div className="card-body">
        {hasGradeInfo && activeTab === 'grade' && gradeInfo && tier ? (
          <div>
            {/* Formula Guide (Collapsible) */}
            {showGuide && (
              <div className="mb-3 p-2 rounded bg-light border">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-semibold text-dark small" style={{ fontSize: '11px' }}>
                    <i className="fa-solid fa-calculator text-primary me-1"></i> Score Calculation Formula:
                  </span>
                  <span className="badge bg-secondary" style={{ fontSize: '9px' }}>Weighted 0-100</span>
                </div>
                <code className="d-block p-1 bg-white rounded border mb-2 text-dark small" style={{ fontSize: '10px' }}>
                  Total = (35% x Quality) + (30% x Completion) + (25% x Commitment) + (10% x Experience)
                </code>
                <div className="row g-1 text-muted" style={{ fontSize: '10px' }}>
                  <div className="col-6"><strong>1. Quality (35%):</strong> Tasks completed with approved status</div>
                  <div className="col-6"><strong>2. Completion (30%):</strong> Completes tasks without unfinished work</div>
                  <div className="col-6"><strong>3. Commitment (25%):</strong> Consistently completes tasks to set targets</div>
                  <div className="col-6"><strong>4. Experience (10%):</strong> Cumulative approved tasks across all projects</div>
                </div>
              </div>
            )}

            {/* Grade Summary Banner */}
            <div
              className="p-3 mb-3 rounded-3 border"
              style={{ backgroundColor: tier.cardBg, borderColor: tier.borderColor, cursor: 'help' }}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-html="true"
              data-bs-custom-class="tooltip-mechanism"
              data-bs-title={gradeThresholdsTooltip}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="badge px-3 py-2 fs-6 fw-bold rounded-pill text-white shadow-xs d-inline-flex align-items-center gap-1"
                    style={{ backgroundColor: tier.badgeBg }}
                  >
                    <i className="fa-solid fa-award"></i>
                    <span>{gradeInfo.overall}</span>
                    <i className="fa-solid fa-circle-info ms-1" style={{ fontSize: '10px', opacity: 0.9 }}></i>
                  </span>
                  <div>
                    <div className="fw-bold text-dark small">{tier.label}</div>
                    <div className="text-muted" style={{ fontSize: '11px', lineHeight: '1.2' }}>
                      {tier.description}
                    </div>
                  </div>
                </div>

                <div className="text-end ps-2 flex-shrink-0">
                  <div className="d-flex align-items-center justify-content-end gap-1 mb-n1">
                    <span
                      className="badge bg-primary text-white"
                      style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px', padding: '2px 5px', borderRadius: '4px' }}
                      title="Trial / Ujicoba"
                    >
                      BETA
                    </span>
                    <span className="fs-3 fw-bold text-dark">{Math.min(100, gradeInfo.score).toFixed(1)}</span>
                  </div>
                  <div className="text-muted small" style={{ fontSize: '10px' }}>/ 100 Pts</div>
                </div>
              </div>

              {/* Update Schedule Note */}
              <div className="mt-2 pt-2 border-top border-secondary-subtle d-flex justify-content-between align-items-center text-muted" style={{ fontSize: '10.5px' }}>
                <span>
                  <i className="fa-regular fa-clock me-1 text-secondary"></i>
                  Score updated daily at 04:00 AM WIB
                </span>
                <span className="badge bg-white text-secondary border" style={{ fontSize: '9px' }}>Daily</span>
              </div>
            </div>

            {/* 4 Pillars Grid (2x2) */}
            <div className="row g-2 mb-3">
              {/* Quality Rate */}
              <div className="col-6">
                <div
                  className="p-2 rounded border bg-light h-100 d-flex flex-column justify-content-between"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-html="true"
                  data-bs-custom-class="tooltip-mechanism"
                  data-bs-title={qualityMechanismTooltip}
                  style={{ cursor: 'help' }}
                >
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold text-dark small" style={{ fontSize: '11px' }}>
                        <i className="fa-solid fa-circle-check text-success me-1"></i> Quality
                      </span>
                      <div className="d-flex align-items-center gap-1">
                        <span className="badge bg-white text-secondary border" style={{ fontSize: '9px' }}>35%</span>
                        <i className="fa-solid fa-circle-info text-secondary" style={{ fontSize: '9px' }}></i>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="fw-bold fs-6 text-dark">{gradeInfo.qualityRate.toFixed(1)}</span>
                      {qualityTier && (
                        <span
                          className="badge text-white"
                          style={{ backgroundColor: qualityTier.badgeBg, fontSize: '9px' }}
                        >
                          Grade {qualityTier.letter}
                        </span>
                      )}
                    </div>
                    <div className="progress mb-1" style={{ height: '5px' }}>
                      <div
                        className="progress-bar rounded"
                        style={{
                          width: `${Math.min(100, Math.max(0, gradeInfo.qualityRate))}%`,
                          backgroundColor: getProgressBarColor(gradeInfo.qualityRate)
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-muted" style={{ fontSize: '10px' }}>Tasks completed with approved status</div>
                </div>
              </div>

              {/* Completion Rate */}
              <div className="col-6">
                <div
                  className="p-2 rounded border bg-light h-100 d-flex flex-column justify-content-between"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-html="true"
                  data-bs-custom-class="tooltip-mechanism"
                  data-bs-title={completionMechanismTooltip}
                  style={{ cursor: 'help' }}
                >
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold text-dark small" style={{ fontSize: '11px' }}>
                        <i className="fa-solid fa-list-check text-info me-1"></i> Completion
                      </span>
                      <div className="d-flex align-items-center gap-1">
                        <span className="badge bg-white text-secondary border" style={{ fontSize: '9px' }}>30%</span>
                        <i className="fa-solid fa-circle-info text-secondary" style={{ fontSize: '9px' }}></i>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="fw-bold fs-6 text-dark">{gradeInfo.completionRate.toFixed(1)}</span>
                      {completionTier && (
                        <span
                          className="badge text-white"
                          style={{ backgroundColor: completionTier.badgeBg, fontSize: '9px' }}
                        >
                          Grade {completionTier.letter}
                        </span>
                      )}
                    </div>
                    <div className="progress mb-1" style={{ height: '5px' }}>
                      <div
                        className="progress-bar rounded"
                        style={{
                          width: `${Math.min(100, Math.max(0, gradeInfo.completionRate))}%`,
                          backgroundColor: getProgressBarColor(gradeInfo.completionRate)
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-muted" style={{ fontSize: '10px' }}>Completes tasks without unfinished work</div>
                </div>
              </div>

              {/* Commitment Rate */}
              <div className="col-6">
                <div
                  className="p-2 rounded border bg-light h-100 d-flex flex-column justify-content-between"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-html="true"
                  data-bs-custom-class="tooltip-mechanism"
                  data-bs-title={commitmentMechanismTooltip}
                  style={{ cursor: 'help' }}
                >
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold text-dark small" style={{ fontSize: '11px' }}>
                        <i className="fa-solid fa-bullseye text-warning me-1"></i> Commitment
                      </span>
                      <div className="d-flex align-items-center gap-1">
                        <span className="badge bg-white text-secondary border" style={{ fontSize: '9px' }}>25%</span>
                        <i className="fa-solid fa-circle-info text-secondary" style={{ fontSize: '9px' }}></i>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <div className="d-flex align-items-center gap-1">
                        <span className="fw-bold fs-6 text-dark">{gradeInfo.commitmentRate.toFixed(1)}</span>
                        {gradeInfo.commitmentRate > 100 && (
                          <span className="badge bg-success-subtle text-success border border-success-subtle" style={{ fontSize: '8px' }}>
                            Bonus
                          </span>
                        )}
                      </div>
                      {commitmentTier && (
                        <span
                          className="badge text-white"
                          style={{ backgroundColor: commitmentTier.badgeBg, fontSize: '9px' }}
                        >
                          Grade {commitmentTier.letter}
                        </span>
                      )}
                    </div>
                    <div className="progress mb-1" style={{ height: '5px' }}>
                      <div
                        className="progress-bar rounded"
                        style={{
                          width: `${Math.min(100, Math.max(0, gradeInfo.commitmentRate))}%`,
                          backgroundColor: getProgressBarColor(gradeInfo.commitmentRate)
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-muted" style={{ fontSize: '10px' }}>Consistently completes tasks to set targets</div>
                </div>
              </div>

              {/* Experience Rate */}
              <div className="col-6">
                <div
                  className="p-2 rounded border bg-light h-100 d-flex flex-column justify-content-between"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-html="true"
                  data-bs-custom-class="tooltip-mechanism"
                  data-bs-title={experienceMechanismTooltip}
                  style={{ cursor: 'help' }}
                >
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold text-dark small" style={{ fontSize: '11px' }}>
                        <i className="fa-solid fa-clock-rotate-left text-primary me-1"></i> Experience
                      </span>
                      <div className="d-flex align-items-center gap-1">
                        <span className="badge bg-white text-secondary border" style={{ fontSize: '9px' }}>10%</span>
                        <i className="fa-solid fa-circle-info text-secondary" style={{ fontSize: '9px' }}></i>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="fw-bold fs-6 text-dark">{gradeInfo.experienceRate.toFixed(1)}</span>
                      {experienceTier && (
                        <span
                          className="badge text-white"
                          style={{ backgroundColor: experienceTier.badgeBg, fontSize: '9px' }}
                        >
                          Grade {experienceTier.letter}
                        </span>
                      )}
                    </div>
                    <div className="progress mb-1" style={{ height: '5px' }}>
                      <div
                        className="progress-bar rounded"
                        style={{
                          width: `${Math.min(100, Math.max(0, gradeInfo.experienceRate))}%`,
                          backgroundColor: getProgressBarColor(gradeInfo.experienceRate)
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-muted d-flex justify-content-between align-items-center" style={{ fontSize: '10px' }}>
                    <span>Approved across all projects: <strong>{gradeInfo.totalApprovedResults ?? 0}</strong></span>
                    <i className="fa-solid fa-circle-info text-secondary ms-1" style={{ fontSize: '9px' }}></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Breakdown (if available) */}
            {gradeProjects && gradeProjects.length > 0 && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="fw-semibold text-dark small" style={{ fontSize: '11px' }}>
                    <i className="fa-solid fa-folder-tree text-secondary me-1"></i> Project Breakdown ({gradeProjects.length})
                  </span>
                </div>
                <div className="table-responsive rounded border" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                  <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: '11px' }}>
                    <thead className="table-light sticky-top">
                      <tr className="text-muted text-uppercase" style={{ fontSize: '9px' }}>
                        <th>Project</th>
                        <th className="text-center">
                          Grade
                          <i
                            className="fa-solid fa-circle-info ms-1 text-secondary"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            data-bs-html="true"
                            data-bs-title="<strong>Project Grade</strong><br/>Shown for project-level reference only. The overall grade is calculated directly from cumulative performance across each evaluation component, not averaged from project grades."
                            title="Shown for project-level reference only. The overall grade is calculated directly from cumulative performance across each evaluation component, not averaged from project grades."
                            style={{ cursor: 'help', fontSize: '9px' }}
                          ></i>
                        </th>
                        <th className="text-center">
                          Score
                          <i
                            className="fa-solid fa-circle-info ms-1 text-secondary"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            data-bs-html="true"
                            data-bs-title="<strong>Project Score</strong><br/>Shown for project-level reference only. The overall score is calculated directly from cumulative performance across each evaluation component, not averaged from project scores."
                            title="Shown for project-level reference only. The overall score is calculated directly from cumulative performance across each evaluation component, not averaged from project scores."
                            style={{ cursor: 'help', fontSize: '9px' }}
                          ></i>
                        </th>
                        <th className="text-center">Quality</th>
                        <th className="text-center">Completion</th>
                        <th className="text-center">Commitment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gradeProjects.map((p, idx) => {
                        const pTier = getGradeConfig(p.overall || p.grade);
                        const qualityTooltip = `<strong>Quality Detail</strong><br/>Approved: <strong>${p.totalApproved ?? 0}</strong><br/>Submitted: <strong>${p.totalSubmitted ?? 0}</strong><br/>Rejected: <strong>${p.totalRejected ?? 0}</strong><br/>Rate: ${p.qualityRate.toFixed(1)}%`;
                        const completionTooltip = `<strong>Completion Detail</strong><br/>Completed: <strong>${p.completedTasks ?? 0}</strong><br/>Reassigned: <strong>${p.droppedTasks ?? 0}</strong><br/>Assigned: <strong>${p.totalAssigned ?? 0}</strong><br/>Rate: ${p.completionRate.toFixed(1)}%`;
                        const commitmentTooltip = `<strong>Commitment Detail</strong><br/>Completed Jobs: <strong>${p.completedTasks ?? 0}</strong><br/>Rate: ${(p.commitmentRate ?? 0).toFixed(1)}%`;
                        const scoreTooltip = `<strong>Score Formula</strong><br/>(35% x Quality) + (30% x Completion) + (25% x Commitment) + (10% x Experience)<br/>Total: ${p.score.toFixed(1)}`;

                        return (
                          <tr key={p.projectId || idx}>
                            <td>
                              <div className="fw-semibold text-dark text-truncate" style={{ maxWidth: '140px' }} title={p.projectName}>
                                {p.projectName || 'Unnamed'}
                              </div>
                            </td>
                            <td className="text-center">
                              <span
                                className="badge text-white"
                                style={{ backgroundColor: pTier.badgeBg, fontSize: '9px', cursor: 'help' }}
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                data-bs-html="true"
                                data-bs-custom-class="tooltip-mechanism"
                                data-bs-title={gradeThresholdsTooltip}
                              >
                                {p.overall || p.grade}
                              </span>
                            </td>
                            <td className="text-center fw-bold text-dark">
                              <span
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                data-bs-html="true"
                                data-bs-title={scoreTooltip}
                                title={`Score: ${Math.min(100, p.score).toFixed(1)}`}
                                style={{ cursor: 'help' }}
                              >
                                {Math.min(100, p.score).toFixed(1)}
                              </span>
                            </td>
                            <td className="text-center">
                              <span
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                data-bs-html="true"
                                data-bs-title={qualityTooltip}
                                title={`Approved: ${p.totalApproved ?? 0} / Submitted: ${p.totalSubmitted ?? 0} (Rejected: ${p.totalRejected ?? 0})`}
                                className="text-dark"
                                style={{
                                  cursor: 'help',
                                  borderBottom: '1px dashed #94a3b8',
                                  paddingBottom: '1px'
                                }}
                              >
                                {p.qualityRate.toFixed(1)}
                              </span>
                            </td>
                            <td className="text-center">
                              <span
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                data-bs-html="true"
                                data-bs-title={completionTooltip}
                                title={`Completed: ${p.completedTasks ?? 0} | Reassigned: ${p.droppedTasks ?? 0} | Assigned: ${p.totalAssigned ?? 0}`}
                                className="text-dark"
                                style={{
                                  cursor: 'help',
                                  borderBottom: '1px dashed #94a3b8',
                                  paddingBottom: '1px'
                                }}
                              >
                                {p.completionRate.toFixed(1)}
                              </span>
                            </td>
                            <td className="text-center">
                              <span
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                data-bs-html="true"
                                data-bs-title={commitmentTooltip}
                                title={`Completed Jobs: ${p.completedTasks ?? 0}`}
                                className="text-dark"
                                style={{
                                  cursor: 'help',
                                  borderBottom: '1px dashed #94a3b8',
                                  paddingBottom: '1px'
                                }}
                              >
                                {(p.commitmentRate ?? 0).toFixed(1)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Device Info Tab Content */
          <div>
            {hasDeviceInfo && userInfo?.deviceInfo ? (
              <div className="row">
                <div className="col-12">
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label className="form-label text-muted mb-1 small">Device Name</label>
                      <div className="fw-semibold">{userInfo.deviceInfo.deviceName || 'N/A'}</div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted mb-1 small">Carrier</label>
                      <div className="fw-semibold">{userInfo.deviceInfo.carrier || 'N/A'}</div>
                    </div>
                  </div>

                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label className="form-label text-muted mb-1 small">Manufacturer</label>
                      <div className="fw-semibold">{userInfo.deviceInfo.manufacturer || 'N/A'}</div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted mb-1 small">Brand/Model</label>
                      <div className="fw-semibold">
                        {(userInfo.deviceInfo.brand || '') + ' ' + (userInfo.deviceInfo.model || '') || 'N/A'}
                      </div>
                    </div>
                  </div>

                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label className="form-label text-muted mb-1 small">OS Version</label>
                      <div className="fw-semibold">
                        {(userInfo.deviceInfo.systemName || '') + ' ' + (userInfo.deviceInfo.systemVersion || '') || 'N/A'}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted mb-1 small">App Version</label>
                      <div className="fw-semibold">{userInfo.deviceInfo.appVersion || 'N/A'}</div>
                    </div>
                  </div>

                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label className="form-label text-muted mb-1 small">Build Number</label>
                      <div className="fw-semibold">{userInfo.deviceInfo.buildNumber || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <i className="fa-solid fa-mobile-screen text-muted fa-3x mb-2"></i>
                <p className="text-muted">No device information available</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDeviceInfoCard;